import { useEffect, useRef } from 'react';
import { IAngularEvent } from 'angular';
import _ from 'lodash';
import { useAIChatDispatch, useAIChatSelector, AIChatMessage } from './Context/AIChatContext';
import { setMessages, setReasoning, LOCAL_STORAGE_KEY } from './Context/Helpers';
import Actions from './Context/Actions';
import LocalStorage from 'Components/Helpers/LocalStorage';
import AIChatResource from 'Resources/AIChat';
import logError from 'App/babel/helpers/logError';

declare const Tools: any;

export const useAIChatListeners = () => {
	const dispatch = useAIChatDispatch();
	const { messages, currentChatId } = useAIChatSelector(({ messages, currentChatId }) => ({
		messages,
		currentChatId
	}));

	const listeners = useRef<(() => void)[]>([]);
	const messageBufferRef = useRef<{
		[messageId: string]: {
			chunks: { sequence: number; content: string; isComplete?: boolean }[];
			chatId: string;
		};
	}>({});
	const lastProcessedSequenceRef = useRef<number>(0);
	const unreadIncrementedForMessageRef = useRef<Record<string, Set<string>>>({});

	const processBufferedChunks = (buffer: (typeof messageBufferRef.current)[string]) => {
		if (!buffer) return;

		buffer.chunks.sort((a, b) => a.sequence - b.sequence);

		let deltaContent = '';
		let nextExpectedSequence = lastProcessedSequenceRef.current + 1;
		let isComplete = false;

		for (const chunk of buffer.chunks) {
			if (chunk.sequence < lastProcessedSequenceRef.current) {
				continue;
			}
			if (chunk.sequence === nextExpectedSequence) {
				if (chunk.content) {
					deltaContent += chunk.content;
				}
				lastProcessedSequenceRef.current = chunk.sequence;
				isComplete = chunk.isComplete ?? false;
				nextExpectedSequence++;
			}
		}

		return { deltaContent, isComplete };
	};

	const clearListeners = () => listeners.current.forEach((unsub: () => void) => unsub());

	useEffect(() => {
		clearListeners();
		listeners.current = [];

		const aiChatStreamListener = Tools.$rootScope.$on(
			'userPrivate.aiChatStreamChunk',
			(
				_ignore: IAngularEvent,
				data: {
					messageId: string;
					content: string;
					sequence: number;
					isComplete?: boolean;
					chatId: string;
					langsmithRunId?: string;
				}
			) => {
				if (data.chatId && data.chatId !== currentChatId) {
					if (!unreadIncrementedForMessageRef.current[data.chatId]) {
						unreadIncrementedForMessageRef.current[data.chatId] = new Set();
					}

					if (data.isComplete && !unreadIncrementedForMessageRef.current[data.chatId].has(data.messageId)) {
						unreadIncrementedForMessageRef.current[data.chatId].add(data.messageId);
						AIChatResource.incrementUnreadCount(data.chatId).catch(err => {
							logError(err, 'Error incrementing unread count');
						});
						dispatch({ type: Actions.INCREMENT_UNREAD_COUNT, chatId: data.chatId });
					}
					return;
				}

				if (!data.chatId || data.chatId !== currentChatId) {
					return;
				}

				let buffer = messageBufferRef.current[data.messageId];
				if (!buffer) {
					messageBufferRef.current[data.messageId] = {
						chunks: [],
						chatId: data.chatId
					};
					buffer = messageBufferRef.current[data.messageId];
					lastProcessedSequenceRef.current = 0;
				}

				if (buffer.chatId && buffer.chatId !== currentChatId) {
					return;
				}

				buffer.chunks.push({
					sequence: data.sequence,
					content: data.content,
					isComplete: data.isComplete
				});
				const { deltaContent, isComplete } = processBufferedChunks(_.cloneDeep(buffer)) ?? {};

				if (messages.length === 0) {
					return;
				}

				const newMessages = [...messages];
				const lastMessage = newMessages[newMessages.length - 1];

				if (!lastMessage) {
					console.warn('AIChat: Received streaming chunk but no message exists to update');
					return;
				}

				if (lastMessage.streaming === false) {
					return;
				}

				lastMessage.streaming = !isComplete;
				if (deltaContent) {
					lastMessage.loading = false;
					lastMessage.response = (lastMessage.response || '') + deltaContent;
				}

				// Store langsmithRunId if provided
				if (data.langsmithRunId && !lastMessage.langsmithRunId) {
					lastMessage.langsmithRunId = data.langsmithRunId;
				}

				dispatch(setMessages(newMessages, data.chatId));
			}
		);

		const reasoningListener = Tools.$rootScope.$on(
			'AIChat.reasoning',
			(_ignore: IAngularEvent, data: { reasoning: string; chatId: string } | string) => {
				const reasoningData = typeof data === 'string' ? { reasoning: data, chatId: undefined } : data;

				if (!reasoningData.chatId) {
					console.warn('Received reasoning without chatId - ignoring to prevent wrong chat assignment');
					return;
				}

				const targetChatId = reasoningData.chatId;

				const localStorage = new LocalStorage();
				const stored = localStorage.getValue(LOCAL_STORAGE_KEY) as string | null;
				let messageId: string | undefined;

				if (stored) {
					try {
						const allChats = JSON.parse(stored) as Record<
							string,
							{ messages: AIChatMessage[]; timestamp: number }
						>;
						const chatMessages = allChats[targetChatId]?.messages || [];

						for (let i = chatMessages.length - 1; i >= 0; i--) {
							if (chatMessages[i].loading || chatMessages[i].streaming) {
								messageId = chatMessages[i].id;
								break;
							}
						}
						if (!messageId && chatMessages.length > 0) {
							messageId = chatMessages[chatMessages.length - 1].id;
						}
					} catch (err) {
						logError(err);
					}
				}

				if (!messageId && targetChatId === currentChatId) {
					for (let i = messages.length - 1; i >= 0; i--) {
						if (messages[i].loading || messages[i].streaming) {
							messageId = messages[i].id;
							break;
						}
					}
					if (!messageId && messages.length > 0) {
						messageId = messages[messages.length - 1].id;
					}
				}

				if (messageId) {
					dispatch(setReasoning(reasoningData.reasoning, targetChatId, messageId));
				}
			}
		);

		listeners.current.push(aiChatStreamListener);
		listeners.current.push(reasoningListener);

		return () => {
			clearListeners();
		};
	}, [dispatch, messages, currentChatId]);
};
